/**
 * Pure market-resolution logic for useRegion, extracted so it can be unit-tested
 * without a DOM/React (the hook wraps this with URL params + localStorage).
 *
 * Resolution order — DELIBERATE:
 *   1. URL `?region=` (deep-link / shareable URL wins)
 *   2. the persisted per-brand choice (survives navigation that drops the param)
 *   3. the brand's primary market (default)
 * `?region=all` → null (All-markets, Overview only). A stored/url market that
 * isn't currently ACTIVE is ignored (falls through to primary) so a stale
 * persisted value can never strand a brand on a market it no longer has.
 *
 * Single-account brands: activeRegions has one entry and primary == it, so this
 * always returns that one market regardless of url/stored — behaviour is
 * identical to before persistence existed (no regression).
 */
/** The minimum of a connected account this module reads. Structural so the
 *  tests can build one without importing the full BrandDetail shape. */
export interface RegionAccount {
  platform?: string | null;
  auth_status?: string | null;
  region?: string | null;
}

/**
 * The brand's ACTIVE markets, derived from its connected accounts.
 *
 * Amazon Ads rows are EXCLUDED. Their `region` is the Amazon Ads API host
 * group — `na` / `eu` / `fe` — not a market code, so including them offers
 * "NA"/"EU" as if they were selectable markets, and flips a single-market
 * brand to multi-region (which reveals the RegionPicker and the /overview
 * nav). One warehouse import of a multi-region brand is enough to do it. The
 * market of an ads row lives in `marketplace`, and it is per-market: a brand
 * with 20 ads profiles has 20 rows, so reading them here would also multiply
 * the picker's options by the number of ad accounts.
 *
 * Storefront platforms keep the existing behaviour: `region` IS their market.
 */
export function activeMarketsFrom(accounts: readonly RegionAccount[] | null | undefined): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const a of accounts ?? []) {
    if (a.auth_status !== "active") continue;
    if (a.platform === "amazon_ads") continue;
    if (!a.region) continue;
    const r = a.region.toUpperCase();
    if (!seen.has(r)) {
      seen.add(r);
      out.push(r);
    }
  }
  return out.sort();
}

export function resolveRegion(
  urlRegion: string | null,
  stored: string | null,
  activeRegions: string[],
  primary: string | null,
): string | null {
  const u = (urlRegion || "").toUpperCase();
  if (u === "ALL") return null; // explicit All countries (Overview only)
  if (u && activeRegions.includes(u)) return u; // URL param wins (deep-link)
  const s = (stored || "").toUpperCase();
  if (s && activeRegions.includes(s)) return s; // then the persisted choice
  return primary; // default = the brand's primary (single-market default)
}
